import React, { useMemo, useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  FileText,
  Download,
  Loader2,
  Search,
  X,
  Settings2,
} from 'lucide-react';
import { FieldSelector } from '@/components/TimesheetReports/FieldSelector';
import type { FieldConfig, FieldPreset } from '@/components/TimesheetReports/FieldSelector';
import { PageHeader } from '@/components/blocks/page-header';
import { useTimesheet } from '@/contexts/TimesheetContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { reportsApi } from '@/services/reportsApi';
import type { PagedTimesheetReportResponse, ReportSummaryDto } from '@/services/reportsApi';
import { rejectionsApi } from '@/services/rejectionsApi';
import { TimesheetReportGrid } from '@/components/TimesheetReportGrid';
import { AdvancedFilterDrawer } from '@/components/TimesheetReports/AdvancedFilterDrawer';
import { QuickFilters } from '@/components/TimesheetReports/QuickFilters';
import { ActiveFilterChips } from '@/components/TimesheetReports/ActiveFilterChips';
import { transformTimesheetsToReportEntries, getReportSummary } from '@/utils/timesheetReportUtils';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import type { TimesheetReportFilters } from '@/types/timesheetReport';
import { toast } from 'sonner';

// Navigation state types
type TimesheetDrilldownState = {
  employeeId: number;
  weekEnding: Date;
};

type AppNavigationState = {
  timesheetDrilldown?: TimesheetDrilldownState;
};

// Main Timesheet Reports Page Component
interface TimesheetReportsPageProps {
  onNavigate?: (path: string, state?: AppNavigationState) => void;
  initialFilters?: TimesheetReportFilters;
}

export const TimesheetReportsPage: React.FC<TimesheetReportsPageProps> = ({ onNavigate, initialFilters }) => {
  const { timesheets, employees, projects, costCentres, currentEmployee } = useTimesheet();
  const { canExportReport, getReportViewAccessLevel } = usePermissions();
  
  // Advanced Filter state - initialize with passed filters if available
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [filters, setFilters] = useState<TimesheetReportFilters>(initialFilters || {});
  const [searchQuery, setSearchQuery] = useState('');

  // Field selector state
  const [fieldPresets, setFieldPresets] = useState<FieldPreset[]>([]);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const [fieldConfiguration, setFieldConfiguration] = useState<FieldConfig[]>([]);

  const { mode, isDemo, isLive } = useScreenDataMode('reports-timesheet');

  const [liveReportData, setLiveReportData] = useState<PagedTimesheetReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update filters when initialFilters change (e.g., when navigating from dashboard)
  useEffect(() => {
    if (initialFilters) {
      setFilters(initialFilters);
    }
  }, [initialFilters]);

  useEffect(() => {
    if (isLive) {
      fetchLiveReportData();
    }
  }, [isLive]);

  const fetchLiveReportData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Backend enforces access-level scoping based on the user's role permissions,
      // so we no longer hard-code employeeIds to only the current user.
      // Users with 'all' access will see all employees, 'team' will see direct reports,
      // and 'own' will only see their own data — all enforced server-side.
      const response = await reportsApi.getTimesheetReport({
        includeSummary: true,
        pageNumber: 1,
        pageSize: 1000,
        sortBy: 'weekEnding',
        sortDir: 'desc'
      });
      setLiveReportData(response);
    } catch (err) {
      const errorMessage = 'Failed to load report data from API';
      setError(errorMessage);
      console.error('Error fetching live report data:', err);
      toast.error('Failed to load report data', {
        description: 'Could not connect to the API. Please ensure the backend is running or switch to demo mode.',
      });
    } finally {
      setLoading(false);
    }
  };

  // Determine if user is a manager (has 'all' or 'team' access level)
  const isManager = useMemo(() => {
    const accessLevel = getReportViewAccessLevel('timesheet');
    return accessLevel === 'all' || accessLevel === 'team';
  }, [getReportViewAccessLevel]);

  // Get timesheets filtered by access level permission
  const allTimesheets = useMemo(() => {
    const accessLevel = getReportViewAccessLevel('timesheet');
    
    let filteredTimesheets = timesheets;
    if (accessLevel === 'own' && currentEmployee) {
      // Only show current employee's timesheets
      filteredTimesheets = timesheets.filter(ts => ts.employeeId === currentEmployee.id);
    } else if (accessLevel === 'team' && currentEmployee) {
      // Show current employee's and their direct reports' timesheets
      const teamMemberIds = employees
        .filter(emp => emp.managerId === currentEmployee.id || emp.id === currentEmployee.id)
        .map(emp => emp.id);
      filteredTimesheets = timesheets.filter(ts => teamMemberIds.includes(ts.employeeId));
    }
    // 'all' access level shows all timesheets (no filtering)
    
    return filteredTimesheets.sort((a, b) => b.weekEnding.getTime() - a.weekEnding.getTime());
  }, [timesheets, currentEmployee, employees, getReportViewAccessLevel]);

  // Transform timesheets to report entries (demo mode)
  const demoReportEntries = useMemo(() => {
    return transformTimesheetsToReportEntries(allTimesheets, employees, projects);
  }, [allTimesheets, employees, projects]);

  // Use live data when available, otherwise use demo data
  const reportEntries = useMemo(() => {
    if (isLive && liveReportData) {
      return liveReportData.items;
    }
    return demoReportEntries;
  }, [isLive, liveReportData, demoReportEntries]);

  // Apply advanced filters and search to report entries
  const filteredReportEntries = useMemo(() => {
    try {
      return reportEntries.filter((entry) => {
        try {
          // Search filter
          if (searchQuery) {
            const query = searchQuery.toLowerCase();
            const searchableFields = [
              entry.employeeName,
              entry.department,
              entry.status,
              entry.projectCode,
              entry.projectName,
              entry.costCentreCode,
              entry.costCentreName,
              entry.description || '',
            ].map(field => String(field).toLowerCase());
            
            if (!searchableFields.some(field => field.includes(query))) {
              return false;
            }
          }
          
          // Date range filter
          if (filters.dateFrom) {
            const fromDate = filters.dateFrom instanceof Date ? filters.dateFrom : new Date(filters.dateFrom);
            if (isNaN(fromDate.getTime())) {
              console.warn('Invalid dateFrom:', filters.dateFrom);
              return true; // Skip this filter if date is invalid
            }
            if (entry.weekEnding < fromDate) return false;
          }
          if (filters.dateTo) {
            const toDate = filters.dateTo instanceof Date ? filters.dateTo : new Date(filters.dateTo);
            if (isNaN(toDate.getTime())) {
              console.warn('Invalid dateTo:', filters.dateTo);
              return true; // Skip this filter if date is invalid
            }
            if (entry.weekEnding > toDate) return false;
          }
          
          // Employee filter
          if (filters.employeeIds && filters.employeeIds.length > 0 && 
              !filters.employeeIds.includes(entry.employeeId)) return false;
          
          // Status filter
          if (filters.statuses && filters.statuses.length > 0 && 
              !filters.statuses.includes(entry.status)) return false;
          
          // Department filter
          if (filters.departments && filters.departments.length > 0 && 
              !filters.departments.includes(entry.department)) return false;
          
          // Project filter (using projectCode as identifier)
          if (filters.projectIds && filters.projectIds.length > 0 && 
              !filters.projectIds.includes(entry.projectCode)) return false;
          
          // Billable filter
          if (filters.billableOnly === true && entry.billable !== true) return false;
          
          // Rejected filter
          if (filters.rejectedOnly && !entry.hasRejections) return false;
          
          return true;
        } catch (entryError) {
          console.error('Error filtering entry:', entryError, entry);
          return true; // Include entry if there's an error filtering it
        }
      });
    } catch (error) {
      console.error('Error in filteredReportEntries:', error);
      toast.error('Error filtering report data');
      return reportEntries; // Return unfiltered data if there's an error
    }
  }, [reportEntries, filters, searchQuery]);

  // Get report summary (based on all data - filtering will be handled by grid)
  const reportSummary = useMemo(() => {
    if (isLive && liveReportData?.summary) {
      return liveReportData.summary;
    }
    return getReportSummary(demoReportEntries);
  }, [isLive, liveReportData, demoReportEntries]);

  // Handle row click to navigate to timesheet detail
  const handleRowClick = (entry: any) => {
    if (onNavigate) {
      // Navigate to Timesheet Grid with the selected employee and week
      onNavigate('/timesheet', {
        timesheetDrilldown: {
          employeeId: entry.employeeId,
          weekEnding: entry.weekEnding
        }
      });
    }
  };

  // Handle CSV export
  const handleExport = () => {
    try {
      // Create CSV header
      const headers = [
        'Employee',
        'Week Ending',
        'Status',
        'Project',
        'Cost Centre',
        'Description',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
        'Total Hours',
        'Billable'
      ];

      // Create CSV rows (export all data - user can filter in grid before exporting)
      const rows = reportEntries.map(entry => [
        entry.employeeName,
        format(entry.weekEnding, 'yyyy-MM-dd'),
        entry.status,
        entry.projectName,
        entry.costCentreName,
        entry.description || '',
        entry.mondayHours.toFixed(2),
        entry.tuesdayHours.toFixed(2),
        entry.wednesdayHours.toFixed(2),
        entry.thursdayHours.toFixed(2),
        entry.fridayHours.toFixed(2),
        entry.saturdayHours.toFixed(2),
        entry.sundayHours.toFixed(2),
        entry.totalHours.toFixed(2),
        entry.billable ? 'Yes' : 'No'
      ]);

      // Add summary row
      rows.push([]);
      rows.push(['Summary']);
      rows.push(['Total Hours', reportSummary.totalHours.toFixed(2)]);
      rows.push(['Billable Hours', reportSummary.billableHours.toFixed(2)]);
      rows.push(['Billable %', `${reportSummary.billablePercentage.toFixed(1)}%`]);
      rows.push(['Total Entries', reportSummary.totalEntries.toString()]);
      rows.push(['Unique Projects', reportSummary.uniqueProjects.toString()]);
      rows.push(['Entries with Rejections', reportSummary.entriesWithRejections.toString()]);

      // Convert to CSV string
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => {
          // Escape cells containing commas or quotes
          const cellStr = String(cell);
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `timesheet-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Export Successful', {
        description: `Exported ${reportEntries.length} timesheet entries to CSV`,
      });
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export Failed', {
        description: 'There was an error exporting the report',
      });
    }
  };

  // Prepare data for Advanced Filter Dialog
  const availableEmployees = useMemo(() => {
    return employees.map(emp => ({
      id: emp.id,
      name: emp.name,
      department: emp.department,
    }));
  }, [employees]);

  const availableDepartments = useMemo(() => {
    return Array.from(new Set(employees.map(emp => emp.department))).sort();
  }, [employees]);

  const availableProjects = useMemo(() => {
    return projects.map(proj => ({
      id: proj.id,
      code: proj.code,
      name: proj.name,
    }));
  }, [projects]);

  const availableCostCentres = useMemo(() => {
    return costCentres.map(cc => ({
      id: cc.id,
      code: cc.code,
      name: cc.name,
    }));
  }, [costCentres]);

  // Count active filters (only from advanced drawer, not quick filters)
  const advancedFilterCount = useMemo(() => {
    let count = 0;
    // Only count filters that are NOT in quick filters
    if (filters.departments && filters.departments.length > 0) count++;
    if (filters.billableOnly) count++;
    if (filters.rejectedOnly) count++;
    // Count date range only if it's a custom range (not from quick filter)
    if ((filters.dateFrom || filters.dateTo) && !filters.quickDateRange) count++;
    return count;
  }, [filters]);

    // Handle removing individual filter
    const handleRemoveFilter = (filterKey: string, value?: string | number) => {
      const newFilters = { ...filters };
    
      switch (filterKey) {
        case 'dateRange':
          delete newFilters.dateFrom;
          delete newFilters.dateTo;
          delete newFilters.quickDateRange;
          break;
        case 'statuses':
          if (value && newFilters.statuses) {
            newFilters.statuses = newFilters.statuses.filter(s => s !== value);
            if (newFilters.statuses.length === 0) delete newFilters.statuses;
          }
          break;
        case 'employeeIds':
          if (value !== undefined && newFilters.employeeIds) {
            const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
            newFilters.employeeIds = newFilters.employeeIds.filter(id => id !== numValue);
            if (newFilters.employeeIds.length === 0) delete newFilters.employeeIds;
          }
          break;
      case 'departments':
        if (value && newFilters.departments) {
          newFilters.departments = newFilters.departments.filter(d => d !== value);
          if (newFilters.departments.length === 0) delete newFilters.departments;
        }
        break;
      case 'projectIds':
        if (value && newFilters.projectIds) {
          newFilters.projectIds = newFilters.projectIds.filter(id => id !== value);
          if (newFilters.projectIds.length === 0) delete newFilters.projectIds;
        }
        break;
      case 'billableOnly':
        delete newFilters.billableOnly;
        break;
      case 'rejectedOnly':
        delete newFilters.rejectedOnly;
        break;
      case 'groupBy':
        if (value && newFilters.groupBy) {
          newFilters.groupBy = newFilters.groupBy.filter(g => g !== value);
          if (newFilters.groupBy.length === 0) delete newFilters.groupBy;
        }
        break;
    }
    
    setFilters(newFilters);
  };

  // Handle clearing all filters
  const handleClearAllFilters = () => {
    setFilters({});
  };

  // Field configuration for FieldSelector
  const storageKey = 'timesheet-report-employee';
  
  // Load field presets from localStorage
  useEffect(() => {
    if (storageKey) {
      try {
        const saved = localStorage.getItem(`field-presets-${storageKey}`);
        if (saved) {
          setFieldPresets(JSON.parse(saved));
        }
      } catch (error) {
        console.error('Failed to load field presets:', error);
      }
    }
  }, [storageKey]);

  // Define field labels (conditionally include department for managers)
  const fieldLabels: Record<string, string> = useMemo(() => {
    const labels: Record<string, string> = {
      weekEnding: 'Week Ending',
      employeeName: 'Employee',
      status: 'Status',
      projectCode: 'Project Code',
      projectName: 'Project Name',
      costCentreCode: 'Cost Centre',
      description: 'Description',
      mondayHours: 'Monday',
      tuesdayHours: 'Tuesday',
      wednesdayHours: 'Wednesday',
      thursdayHours: 'Thursday',
      fridayHours: 'Friday',
      saturdayHours: 'Saturday',
      sundayHours: 'Sunday',
      totalHours: 'Total Hours',
      billable: 'Billable',
      hasRejections: 'Rejections',
    };
    
    // Only include department field for managers
    if (isManager) {
      labels.department = 'Department';
    }
    
    return labels;
  }, [isManager]);

  const fieldCategories: Record<string, string> = useMemo(() => {
    const categories: Record<string, string> = {
      weekEnding: 'Time Period',
      employeeName: 'People',
      status: 'Status',
      projectCode: 'Project',
      projectName: 'Project',
      costCentreCode: 'Finance',
      description: 'Details',
      mondayHours: 'Daily Hours',
      tuesdayHours: 'Daily Hours',
      wednesdayHours: 'Daily Hours',
      thursdayHours: 'Daily Hours',
      fridayHours: 'Daily Hours',
      saturdayHours: 'Daily Hours',
      sundayHours: 'Daily Hours',
      totalHours: 'Summary',
      billable: 'Finance',
      hasRejections: 'Status',
    };
    
    // Only include department category for managers
    if (isManager) {
      categories.department = 'People';
    }
    
    return categories;
  }, [isManager]);

  // Create default field configs (always in default order)
  const defaultFieldConfigs: FieldConfig[] = useMemo(() => {
    const allFields = Object.keys(fieldLabels);
    return allFields.map((id, index) => ({
      id,
      label: fieldLabels[id],
      visible: true, // All visible by default
      order: index,
      category: fieldCategories[id],
    }));
  }, [fieldLabels, fieldCategories]);

  // Create field configs (reflects current state)
  const fieldConfigs: FieldConfig[] = useMemo(() => {
    // If we have a saved field configuration, use its order
    // But filter out any fields that are no longer available (e.g., department for non-managers)
    if (fieldConfiguration.length > 0) {
      return fieldConfiguration
        .filter((field) => fieldLabels[field.id]) // Only include fields that exist in fieldLabels
        .map((field) => ({
          ...field,
          visible: columnVisibility[field.id] !== false,
        }));
    }
    
    // Otherwise, use default order
    return defaultFieldConfigs.map((field) => ({
      ...field,
      visible: columnVisibility[field.id] !== false,
    }));
  }, [columnVisibility, fieldConfiguration, fieldLabels, defaultFieldConfigs]);

  const handleFieldsChange = (fields: FieldConfig[]) => {
    const newVisibility: Record<string, boolean> = {};
    fields.forEach((field) => {
      newVisibility[field.id] = field.visible;
    });
    setColumnVisibility(newVisibility);
    setFieldConfiguration(fields);
  };

  const handleSavePreset = (preset: FieldPreset) => {
    const newPresets = [...fieldPresets, preset];
    setFieldPresets(newPresets);
    if (storageKey) {
      localStorage.setItem(`field-presets-${storageKey}`, JSON.stringify(newPresets));
    }
  };

  const handleDeletePreset = (presetId: string) => {
    const newPresets = fieldPresets.filter((p) => p.id !== presetId);
    setFieldPresets(newPresets);
    if (storageKey) {
      localStorage.setItem(`field-presets-${storageKey}`, JSON.stringify(newPresets));
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-start justify-between">
        <PageHeader
          title="Timesheet Reports"
          subtitle="Comprehensive timesheet analysis and reporting"
          size={36}
          showActions={false}
          showBreadcrumb={false}
          icon={<FileText />}
        />
        <div className="flex items-center gap-2">
          <Button 
            onClick={handleExport} 
            variant="outline" 
            className="gap-2"
            disabled={!canExportReport('timesheet')}
            title={!canExportReport('timesheet') ? "You don't have permission to export timesheet reports" : undefined}
          >
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Data Grid */}
      <Card>
        <CardContent className="pt-6 pb-3 px-6">
          {/* Top level layout: Search, Filters, Actions */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4 -mx-6 px-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search timesheets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setSearchQuery('')}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            
            <div className="lg:flex-shrink-0">
              <QuickFilters
              filters={filters}
              onFiltersChange={setFilters}
              onAdvancedClick={() => setShowAdvancedFilter(true)}
              advancedFilterCount={advancedFilterCount}
              customActions={
                <FieldSelector
                  fields={fieldConfigs}
                  defaultFields={defaultFieldConfigs}
                  onFieldsChange={handleFieldsChange}
                  storageKey={storageKey}
                  presets={fieldPresets}
                  onSavePreset={handleSavePreset}
                  onDeletePreset={handleDeletePreset}
                />
              }
            />
            </div>
          </div>

          {/* Active Filter Chips */}
          <ActiveFilterChips
            filters={filters}
            onRemoveFilter={handleRemoveFilter}
            onClearAll={handleClearAllFilters}
            availableEmployees={availableEmployees}
            availableProjects={availableProjects}
            availableDepartments={availableDepartments}
          />



          {isLive && loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center space-y-3">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Loading report data...</p>
              </div>
            </div>
          )}
          
          {isLive && error && !loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center space-y-3">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                <Button onClick={fetchLiveReportData} variant="outline" size="sm">
                  Retry
                </Button>
              </div>
            </div>
          )}
          
          {(!isLive || (!loading && !error)) && (
            <TimesheetReportGrid
              data={filteredReportEntries}
              isManager={isManager}
              onRowClick={handleRowClick}
              columnVisibility={columnVisibility}
              fieldConfig={fieldConfiguration.length > 0 ? fieldConfiguration : undefined}
              groupBy={filters.groupBy}
            />
          )}
        </CardContent>
      </Card>

      {/* Advanced Filter Drawer */}
      <AdvancedFilterDrawer
        open={showAdvancedFilter}
        onOpenChange={setShowAdvancedFilter}
        filters={filters}
        onFiltersChange={setFilters}
        availableEmployees={availableEmployees}
        availableDepartments={availableDepartments}
        availableProjects={availableProjects}
        availableCostCentres={availableCostCentres}
      />
    </div>
  );
};
